/* eslint-disable react/require-default-props */

import React, { useMemo, useState } from "react";
import moment from "moment";
import Model, { Providers } from "@tripian/model";

import TimelineGMap from "../TimelineGMap";
import MapSearch from "../../../TripPlan/layouts/MapLayout/MapSearch/MapSearch";
import CurrentLocation from "../../../TripPlan/layouts/MapLayout/CurrentLocation/CurrentLocation";
import ShowAllAlternatives from "../../../TripPlan/layouts/MapLayout/ShowAllAlternatives/ShowAllAlternatives";
import ShowSearchOffers from "../../../TripPlan/layouts/MapLayout/ShowSearchOffers/ShowSearchOffers";
import ShowAccommodations from "../../../TripPlan/layouts/MapLayout/ShowAccommodations/ShowAccommodations";
import ShowCarRentOffers from "../../../TripPlan/layouts/MapLayout/ShowCarRentOffers/ShowCarRentOffers";
import PlanRoute from "../../../TripPlan/layouts/MapLayout/PlanRoute/PlanRoute";
import ShowProviderTours from "../../../TripPlan/layouts/MapLayout/ShowProviderTours/ShowProviderTours";
import ShowProviderVideos from "../../../TripPlan/layouts/MapLayout/ShowProviderVideos/ShowProviderVideos";
import ShowProviderEvents from "../../../TripPlan/layouts/MapLayout/ShowProviderEvents/ShowProviderEvents";
import ShowProviderStorages from "../../../TripPlan/layouts/MapLayout/ShowProviderStorages/ShowProviderStorages";

import useTrip from "../../../../hooks/useTrip";
import usePlan from "../../../../hooks/usePlan";
import { MapCategory } from "@tripian/react";
import useRezdyApi from "../../../../hooks/useRezdyApi";
import useVidereoApi from "../../../../hooks/useVidereoApi";
import useVictoryEvents from "../../../../hooks/useVictoryEvents";
import { useGuestUser } from "../../../../hooks/useGuestUser";
import useRadicalStorage from "../../../../hooks/useRadicalStorage";
import { TimelineSegmentGroup } from "../../../../utils/timelineAdapters";
import classes from "../../../TripPlan/layouts/MapLayout/MapLayout.module.scss";

interface ITimelineMapLayout {
  tripReference: Model.TripReference;
  segmentGroups: TimelineSegmentGroup[];
  activeSegmentIndex: number | null;
  fullWidth: boolean;
  alternativesStepId?: number;
  poiCategoryGroups: Model.PoiCategoryGroup[];
  offersSearchVisible: boolean;
  setOffersSearchVisible: (newVisible: boolean) => void;
  loadingSearchOffers: boolean;
  offersResult: Model.Poi[];
  searchOffer: () => Promise<Model.Poi[]>;
  selectedPoiCategoryIds: number[];
  setSelectedPoiCategoryIds: (selectedCategoryIds: number[]) => void;
  sharedTrip?: boolean;
  eventCardClicked: (eventid: number) => void;
  setShowCloneModal: () => void;
  onAddStorageToItinerary: (
    storage: Providers.RadicalStorage.StorageItem
  ) => void;
  t: (value: Model.TranslationKey) => string;
}

const TimelineMapLayout: React.FC<ITimelineMapLayout> = ({
  tripReference,
  segmentGroups,
  activeSegmentIndex,
  fullWidth,
  alternativesStepId,
  poiCategoryGroups,
  offersSearchVisible,
  setOffersSearchVisible,
  loadingSearchOffers,
  offersResult,
  searchOffer,
  selectedPoiCategoryIds,
  setSelectedPoiCategoryIds,
  sharedTrip = false,
  eventCardClicked,
  setShowCloneModal,
  onAddStorageToItinerary,
  t,
}) => {
  const { tripReadOnly } = useTrip();
  const { plans } = usePlan();

  const isGuest = useGuestUser();

  const { loadingRezdyTours, rezdyProducts } = useRezdyApi(
    tripReference.city.id
  );
  const { loadingVidereoVideos, videreoVideos } = useVidereoApi(
    tripReference.city.id
  );
  const { loadingStorageList, storageList } = useRadicalStorage(
    tripReference.city.id
  );

  const { loadingVictoryEventCatalog, victoryEvents } = useVictoryEvents(
    tripReference.city.coordinate.lat,
    tripReference.city.coordinate.lng,
    tripReference.tripProfile.arrivalDatetime,
    tripReference.tripProfile.departureDatetime
  );

  const [showAllalternatives, setShowAllalternatives] = useState<boolean>(false);
  const [searchThisAreaPois, setSearchThisAreaPois] = useState<Model.Poi[]>([]);
  const [showAccommodations, setShowAccommodations] = useState<boolean>(false);
  const [showCarRentOffers, setShowCarRentOffers] = useState<boolean>(false);
  const [showProviderTours, setShowProviderTours] = useState<boolean>(false);
  const [showProviderVideos, setShowProviderVideos] = useState<boolean>(false);
  const [showProviderEvents, setShowProviderEvents] = useState<boolean>(false);
  const [showProviderStorages, setShowProviderStorages] = useState(false);

  const mapClasses = [classes.mapContainer];
  if (!window.tconfig.SHOW_OVERVIEW) mapClasses.push(classes.containerHeight);
  else mapClasses.push(classes.containerHeightTab);
  if (fullWidth) mapClasses.push(classes.wm100);

  const isWithinBounds = (
    entry: { lat: number; lng: number },
    bounds: { north: number; south: number; west: number; east: number }
  ): boolean => {
    return (
      bounds.south <= entry.lat &&
      entry.lat <= bounds.north &&
      bounds.west <= entry.lng &&
      entry.lng <= bounds.east
    );
  };

  const filteredVideos = useMemo(() => {
    return videreoVideos.filter(entry =>
      isWithinBounds(entry, {
        north: tripReference.city.boundary[1],
        south: tripReference.city.boundary[0],
        west: tripReference.city.boundary[2],
        east: tripReference.city.boundary[3],
      })
    );
  }, [videreoVideos, tripReference.city.boundary]);

  const googleMap = useMemo(
    () => (
      <TimelineGMap
        segmentGroups={segmentGroups}
        activeSegmentIndex={activeSegmentIndex}
        showAllalternatives={showAllalternatives}
        alternativesStepId={alternativesStepId}
        searchThisAreaPois={searchThisAreaPois}
        showAccommodations={showAccommodations}
        showStorages={showProviderStorages}
        showCarRentOffers={showCarRentOffers}
        showProviderTours={showProviderTours}
        showProviderVideos={showProviderVideos}
        showProviderEvents={showProviderEvents}
        cycling={tripReference.tripProfile.answers.includes(407)}
        hideRoutes={sharedTrip && window.tconfig.WIDGET_THEME_1}
        selectedPoiCategoryIds={selectedPoiCategoryIds}
        rezdyProducts={rezdyProducts}
        videreoVideos={filteredVideos}
        victoryEvents={victoryEvents}
        radicalStorages={storageList}
        eventCardClicked={eventCardClicked}
        onAddStorageToItinerary={onAddStorageToItinerary}
        t={t}
      />
    ),
    [
      segmentGroups,
      activeSegmentIndex,
      alternativesStepId,
      searchThisAreaPois,
      sharedTrip,
      showAccommodations,
      showAllalternatives,
      showCarRentOffers,
      showProviderTours,
      showProviderVideos,
      showProviderEvents,
      tripReference.tripProfile.answers,
      selectedPoiCategoryIds,
      rezdyProducts,
      filteredVideos,
      victoryEvents,
      storageList,
      showProviderStorages,
      eventCardClicked,
      onAddStorageToItinerary,
      t,
    ]
  );

  const showShowAccommodations = useMemo(() => {
    const startTripDatetime = moment(
      tripReference.tripProfile.arrivalDatetime
    ).format("X");
    const datetimeNow = moment(new Date()).format("X");
    return (
      window.tconfig.SHOW_ACCOMMODATION_POIS && datetimeNow < startTripDatetime
    );
  }, [tripReference]);

  const showShowCarRentOffes = useMemo(() => {
    const startTripDatetime = moment(
      tripReference.tripProfile.arrivalDatetime
    ).format("X");
    const datetimeNow = moment(new Date()).format("X");
    return window.tconfig.SHOW_CAR_RENT_POIS && datetimeNow < startTripDatetime;
  }, [tripReference]);

  const showShowProviderTours = useMemo(() => {
    const endTripDatetime = moment(
      tripReference.tripProfile.departureDatetime
    ).format("X");
    const datetimeNow = moment(new Date()).format("X");
    return (
      window.tconfig.TOUR_TICKET_PROVIDER_IDS.some(
        x => x === Model.PROVIDER_ID.REZDY
      ) &&
      (tripReference.city.id === 625 || tripReference.city.id === 341) &&
      datetimeNow < endTripDatetime
    );
  }, [tripReference]);

  const showShowProviderEvents = useMemo(() => {
    const endTripDatetime = moment(
      tripReference.tripProfile.departureDatetime
    ).format("X");
    const datetimeNow = moment(new Date()).format("X");
    return (
      window.tconfig.TOUR_TICKET_PROVIDER_IDS.some(
        x => x === Model.PROVIDER_ID.VICTORY
      ) && datetimeNow < endTripDatetime
    );
  }, [tripReference]);

  const showShowProviderStorages = useMemo(() => {
    const endTripDatetime = moment(
      tripReference.tripProfile.departureDatetime
    ).format("X");
    const datetimeNow = moment(new Date()).format("X");

    return (
      window.tconfig.ACCOMMODATION_PROVIDER_IDS.some(
        x => x === Model.PROVIDER_ID.RADICAL_STORAGE
      ) && datetimeNow < endTripDatetime
    );
  }, [tripReference.tripProfile.departureDatetime]);

  const showShowProviderVideos = useMemo(() => {
    const isCorrectDomain =
      window.location.hostname === "trial-dev.tripian.com";
    return tripReference.city.id === 659 && isCorrectDomain;
  }, [tripReference]);

  // Get first available plan for PlanRoute
  const firstAvailablePlan = useMemo(() => {
    if (!plans || plans.length === 0) return null;
    return plans.find(p => p.generatedStatus !== 0) || plans[0];
  }, [plans]);

  return (
    <div className={mapClasses.join(" ")}>
      {googleMap}
      <CurrentLocation />
      {sharedTrip && window.tconfig.WIDGET_THEME_1 && plans ? (
        <>
          <div className={classes.mapCategories}>
            <MapCategory
              categoryGroups={poiCategoryGroups.filter(poiCategory =>
                plans[0].steps.some(step =>
                  step.poi.category.some(category =>
                    poiCategory.categories.some(c => c.id === category.id)
                  )
                )
              )}
              selectedPoiCategoryIds={selectedPoiCategoryIds}
              setSelectedPoiCategoryIds={(newIndex: number[]) =>
                setSelectedPoiCategoryIds(newIndex)
              }
              clearCategories={() => setSelectedPoiCategoryIds([])}
              t={t}
            />
          </div>
          <div className={classes.mapLeftTopContainer}>
            {window.tconfig.SHOW_OFFERS && (
              <ShowSearchOffers
                show={offersSearchVisible}
                setShow={setOffersSearchVisible}
                setSearchThisAreaPois={setSearchThisAreaPois}
                loadingSearchOffers={loadingSearchOffers}
                offersResult={offersResult}
                searchOffer={searchOffer}
              />
            )}
          </div>
        </>
      ) : (
        <>
          <MapSearch
            show
            setSearchThisAreaPois={setSearchThisAreaPois}
            poiCategoryGroups={poiCategoryGroups}
            toggleStorages={setShowProviderStorages}
            showShowProviderStorages={showShowProviderStorages}
          />

          <ShowAllAlternatives
            show={showAllalternatives}
            setShow={setShowAllalternatives}
          />
          <div className={classes.mapLeftTopContainer}>
            {window.tconfig.SHOW_OFFERS && tripReadOnly === false && (
              <ShowSearchOffers
                show={offersSearchVisible}
                setShow={setOffersSearchVisible}
                setSearchThisAreaPois={setSearchThisAreaPois}
                loadingSearchOffers={loadingSearchOffers}
                offersResult={offersResult}
                searchOffer={searchOffer}
              />
            )}
            {showShowAccommodations && (
              <ShowAccommodations
                show={showAccommodations}
                setShow={setShowAccommodations}
              />
            )}
            {showShowCarRentOffes && (
              <ShowCarRentOffers
                show={showCarRentOffers}
                setShow={setShowCarRentOffers}
              />
            )}
            {showShowProviderTours && (
              <ShowProviderTours
                loading={loadingRezdyTours}
                show={showProviderTours}
                setShow={setShowProviderTours}
              />
            )}
            {showShowProviderVideos && (
              <ShowProviderVideos
                loading={loadingVidereoVideos}
                show={showProviderVideos}
                setShow={setShowProviderVideos}
              />
            )}
            {showShowProviderEvents && (
              <ShowProviderEvents
                loading={loadingVictoryEventCatalog}
                show={showProviderEvents}
                setShow={setShowProviderEvents}
              />
            )}
          </div>
        </>
      )}
      {firstAvailablePlan && isGuest === false && (
        <PlanRoute
          planId={firstAvailablePlan.id}
          tripHash={tripReference.tripHash}
          setShowCloneModal={setShowCloneModal}
        />
      )}
    </div>
  );
};

export default TimelineMapLayout;
